import { NetworkParams, RestService } from "@sinossi/mates-react-library";
import {
  Conversation,
  Message,
  ConversationFilter,
  MessageFilter,
  CreateConversationRequest,
  SendMessageRequest,
  ConversationSummary,
  PaginatedResponse
} from "models";

export class MessageService {
  private static readonly BASE_URL = '/api/messages';

  /**
   * Converte in modo sicuro un contenuto di messaggio in testo leggibile
   */
  private static extractTextFromContent(content: any): string {
    if (typeof content === 'string') {
      return content;
    }

    if (content && typeof content === 'object') {
      // Gestione Draft.js RawDraftContentState
      if (content.blocks && Array.isArray(content.blocks)) {
        try {
          return content.blocks
            .map((block: any) => block.text || '')
            .filter((text: string) => text.trim() !== '')
            .join(' ')
            .substring(0, 100) + (content.blocks.some((block: any) => (block.text || '').length > 100) ? '...' : '');
        } catch (error) {
          console.warn('Errore nell\'estrazione del testo da Draft.js:', error);
          return 'Messaggio multimediale';
        }
      }

      // Altri formati di contenuto
      if (content.text) {
        return typeof content.text === 'string' ? content.text : 'Messaggio multimediale';
      }

      if (content.content) {
        return this.extractTextFromContent(content.content);
      }

      return 'Messaggio multimediale';
    }

    return '';
  }

  /**
   * Recupera le conversazioni dell'utente corrente
   */
  static getUserConversations = (filter: ConversationFilter, callback: any, catchCallback: any) => {
    RestService
      .post<PaginatedResponse<Conversation>>(`${this.BASE_URL}/conversations`, filter)
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Crea una nuova conversazione
   */
  static createConversation = (request: CreateConversationRequest, callback: any, catchCallback: any) => {
    RestService
      .post<{ id: string }>(`${this.BASE_URL}/conversations/create`, request)
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Recupera i dettagli di una conversazione
   */
  static getConversation = (conversationId: string, callback: any, catchCallback: any) => {
    RestService
      .get<Conversation>(`${this.BASE_URL}/conversations/${conversationId}`)
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Archivia una conversazione
   */
  static archiveConversation = (conversationId: string, callback: any, catchCallback: any) => {
    RestService
      .delete(`${this.BASE_URL}/conversations/${conversationId}`)
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Elimina la conversazione solo per l'utente corrente (soft delete per-utente)
   */
  static deleteConversationForUser = (conversationId: string, callback: any, catchCallback: any) => {
    RestService
      .delete(`${this.BASE_URL}/conversations/${conversationId}/leave`)
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Recupera i messaggi di una conversazione
   */
  static getConversationMessages = (
    conversationId: string,
    filter: MessageFilter,
    callback: any,
    catchCallback: any
  ) => {
    RestService
      .post<PaginatedResponse<Message>>(`${this.BASE_URL}/conversations/${conversationId}/messages`, filter)
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Invia un nuovo messaggio
   */
  static sendMessage = (request: SendMessageRequest, callback: any, catchCallback: any) => {
    RestService
      .post<{ id: string }>(`${this.BASE_URL}/send`, request)
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Invia un nuovo messaggio con allegato
   */
  static sendMessageWithAttachment = (request: SendMessageRequest, attachment: File | null, callback: any, catchCallback: any) => {
    const formData = new FormData();
    formData.append('request', JSON.stringify(request));

    if (attachment) {
      formData.append('attachment', attachment);
    }

    const params = new NetworkParams()
    params.contentType = undefined

    RestService
      .genericPost<any, any>(`${this.BASE_URL}/send-with-attachment`, formData, params)
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Marca una conversazione come letta
   */
  static markConversationAsRead = (conversationId: string, callback: any, catchCallback: any) => {
    RestService
      .put(`${this.BASE_URL}/conversations/${conversationId}/read`, {})
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Recupera il numero di messaggi non letti
   */
  static getUnreadMessagesCount = (callback: any, catchCallback: any) => {
    RestService
      .get<number>(`${this.BASE_URL}/unread-count`)
      .then((response: any) => {
        try {
          // Gestisce sia response diretta che response wrappata
          let count = response;

          // Se la risposta è un oggetto con data, estrai il valore
          if (response && typeof response === 'object' && response.data !== undefined) {
            count = response.data;
          }

          // Assicurati che sia un numero valido
          const validCount = typeof count === 'number' && !isNaN(count) && count >= 0 ? count : 0;
          callback(validCount);
        } catch (error) {
          console.error('Errore nel processare il conteggio messaggi non letti:', error);
          callback(0);
        }
      })
      .catch((error: any) => {
        console.error('Errore nel caricamento del conteggio messaggi non letti:', error);
        callback(0); // Ritorna 0 in caso di errore
      });
  }

  /**
   * Cerca conversazioni
   */
  static searchConversations = (
    searchTerm: string,
    filter: ConversationFilter,
    callback: any,
    catchCallback: any
  ) => {
    RestService
      .post<PaginatedResponse<Conversation>>(
        `${this.BASE_URL}/conversations/search?searchTerm=${encodeURIComponent(searchTerm)}`,
        filter
      )
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Recupera un riassunto delle conversazioni per la visualizzazione nell'header
   */
  static getConversationsSummary = (limit: number = 5, callback: any, catchCallback: any) => {
    const filter: ConversationFilter = {
      page: 0,
      size: limit,
      sortBy: 'lastMessageAt',
      sortDirection: 'DESC',
      isArchived: false
    };

    this.getUserConversations(filter, (response: any) => {
      // Verifica che la risposta sia valida
      if (!response || !response.data.items || !Array.isArray(response.data.items)) {
        callback([]);
        return;
      }

      const summaries: ConversationSummary[] = response.data.items.map((conv: Conversation) => {
        // Gestione sicura del lastMessage usando la funzione di utilità
        const lastMessageText = this.extractTextFromContent(conv.lastMessage);

        // Gestione sicura dei partecipanti
        const participantNames: string[] = [];
        if (Array.isArray(conv.participants)) {
          conv.participants.forEach((p: any) => {
            try {
              if (p && typeof p === 'object') {
                if (p.account?.accountDetails?.firstName && p.account?.accountDetails?.lastName) {
                  participantNames.push(`${p.account.accountDetails.firstName} ${p.account.accountDetails.lastName}`);
                } else if (p.account?.username && typeof p.account.username === 'string') {
                  participantNames.push(p.account.username);
                } else if (p.accountId && typeof p.accountId === 'string') {
                  participantNames.push(p.accountId);
                }
              }
            } catch (error) {
              console.warn('Errore nel processare partecipante:', error);
            }
          });
        }

        return {
          id: typeof conv.id === 'string' ? conv.id : '',
          subject: typeof conv.subject === 'string' ? conv.subject : 'Conversazione senza titolo',
          lastMessage: lastMessageText,
          lastMessageAt: typeof conv.lastMessageAt === 'string' ? conv.lastMessageAt : new Date().toISOString(),
          unreadCount: typeof conv.unreadCount === 'number' ? conv.unreadCount : 0,
          participants: participantNames,
          isArchived: typeof conv.isArchived === 'boolean' ? conv.isArchived : false
        };
      });
      callback(summaries);
    }, (error: any) => {
      // In caso di errore, passa un array vuoto invece dell'errore
      console.error('Errore nel caricamento delle conversazioni:', error);
      callback([]);
    });
  }

  /**
   * Polling per aggiornamenti in tempo reale
   */
  static pollForUpdates = (lastCheckTime: string, callback: any, catchCallback: any) => {
    // Implementazione semplificata - in produzione si potrebbe usare WebSocket
    this.getUnreadMessagesCount((currentUnreadCount: number) => {
      callback({
        hasNewMessages: currentUnreadCount > 0,
        unreadCount: currentUnreadCount,
        updatedConversations: [] // Da implementare con logica più sofisticata
      });
    }, catchCallback);
  }

  /**
   * Recupera gli utenti disponibili per l'invio di messaggi
   */
  static getAvailableRecipients = (callback: any, catchCallback: any, page: number = 0, size: number = 50, searchTerm?: string) => {
    let url = `${this.BASE_URL}/available-recipients?page=${page}&size=${size}`;
    if (searchTerm && searchTerm.trim()) {
      url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    }

    RestService
      .get<PaginatedResponse<any>>(url)
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Carica un allegato per i messaggi
   */
  static uploadAttachment = (file: File, callback: any, catchCallback: any) => {
    const formData = new FormData();
    formData.append('file', file);

    RestService
      .post<{ id: string; fileName: string; fileSize: number; mimeType: string }>(`${this.BASE_URL}/attachments/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Elimina un allegato
   */
  static deleteAttachment = (attachmentId: string, callback: any, catchCallback: any) => {
    RestService
      .delete(`${this.BASE_URL}/attachments/${attachmentId}`)
      .then(callback)
      .catch(catchCallback);
  }

  /**
   * Scarica un allegato
   */
  static downloadAttachment = (attachmentId: string, fileName: string) => {
    const url = `${this.BASE_URL}/attachments/${attachmentId}/download`;
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}